import { useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import type { PersonaOption } from "./PersonaSelectorSheet";
import type { VoiceGender } from "../config/voicePersonaConfig";

// Per-persona gradient palette — matched to design mockup
const PERSONA_COLORS: Record<string, { from: string; to: string }> = {
  cheerleader:     { from: "#E8554A", to: "#F5A07A" },  // red/coral → peach
  listener:        { from: "#7A3CD2", to: "#B478F5" },  // purple → light violet
  active_advisor:  { from: "#2AB8A0", to: "#6EDCC8" },  // teal green → light teal
  sleep_companion: { from: "#4A7CF5", to: "#8EB5FF" },  // royal blue → sky blue
};

const DEFAULT_COLOR = { from: "#4a7cf5", to: "#8eafff" };

interface PersonaCarouselSheetProps {
  show: boolean;
  options: PersonaOption[];
  selectedPersonaId: string;
  selectedGender: VoiceGender;
  onSelect: (personaId: string, gender: VoiceGender) => void;
  onClose: () => void;
  title: string;
  maleLabel: string;
  femaleLabel: string;
  selectLabel: string;
}

export function PersonaCarouselSheet({
  show,
  options,
  selectedPersonaId,
  selectedGender,
  onSelect,
  onClose,
  title,
  maleLabel,
  femaleLabel,
  selectLabel,
}: PersonaCarouselSheetProps) {
  // Derive initial index from selectedPersonaId
  const initialIndex = Math.max(
    0,
    options.findIndex((o) => o.id === selectedPersonaId),
  );

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [pendingGender, setPendingGender] = useState<VoiceGender>(selectedGender);

  // Reset when sheet opens to reflect current state
  const handleOpen = useCallback(() => {
    const idx = Math.max(
      0,
      options.findIndex((o) => o.id === selectedPersonaId),
    );
    setActiveIndex(idx);
    setPendingGender(selectedGender);
  }, [options, selectedPersonaId, selectedGender]);

  const handlePrev = () => {
    setActiveIndex((i) => (i - 1 + options.length) % options.length);
  };

  const handleNext = () => {
    setActiveIndex((i) => (i + 1) % options.length);
  };

  const handleSelect = () => {
    const persona = options[activeIndex];
    if (persona) {
      onSelect(persona.id, pendingGender);
    }
  };

  if (typeof document === "undefined") return null;

  const activePersona = options[activeIndex];
  const colors = activePersona
    ? (PERSONA_COLORS[activePersona.id] ?? DEFAULT_COLOR)
    : DEFAULT_COLOR;

  return createPortal(
    <AnimatePresence onExitComplete={() => {}}>
      {show && (
        <motion.div
          className="fixed inset-0 z-[9999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onAnimationStart={() => {
            if (show) handleOpen();
          }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={onClose}
          />

          {/* Sheet panel */}
          <motion.div
            className="absolute inset-x-0 bottom-0 rounded-t-[32px] overflow-hidden flex flex-col"
            style={{
              height: "85vh",
              zIndex: 9999,
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
          >
            {/* Animated gradient background */}
            <motion.div
              className="absolute inset-0"
              animate={{
                background: `linear-gradient(160deg, ${colors.from} 0%, ${colors.to} 100%)`,
              }}
              transition={{ duration: 0.45, ease: "easeInOut" }}
            />

            {/* Animated SVG Wave */}
            <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none" style={{ height: "45%" }}>
              <style>{`
                @keyframes waveFlow1 {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                @keyframes waveFlow2 {
                  0% { transform: translateX(-50%); }
                  100% { transform: translateX(0); }
                }
              `}</style>
              {/* Wave layer 1 - slower, more opaque */}
              <div
                className="absolute inset-0"
                style={{ animation: "waveFlow1 8s linear infinite", width: "200%" }}
              >
                <svg viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
                  <path
                    d="M0,160 C180,240 360,80 540,160 C720,240 900,80 1080,160 C1260,240 1440,80 1440,160 L1440,320 L0,320 Z"
                    fill="rgba(255,255,255,0.18)"
                  />
                </svg>
              </div>
              {/* Wave layer 2 - faster, less opaque */}
              <div
                className="absolute inset-0"
                style={{ animation: "waveFlow2 5s linear infinite", width: "200%" }}
              >
                <svg viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
                  <path
                    d="M0,200 C200,120 400,260 600,200 C800,120 1000,260 1200,200 C1350,150 1440,220 1440,200 L1440,320 L0,320 Z"
                    fill="rgba(255,255,255,0.10)"
                  />
                </svg>
              </div>
              {/* Bottom solid fill to close the wave */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-white/10" />
            </div>


            {/* Content */}
            <div className="relative flex flex-col h-full px-6 pt-5 pb-0"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)" }}>

              {/* Drag Handle */}
              <div className="mx-auto w-10 h-1 rounded-full bg-white/40 mb-6" />

              {/* Title */}
              <h2 className="text-center text-sm font-medium text-white/80 tracking-wide mb-8">
                {title}
              </h2>

              {/* Carousel Main Area */}
              <div className="flex-1 flex items-center justify-between gap-2 min-h-0">
                {/* Left Arrow */}
                <button
                  onClick={handlePrev}
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white active:bg-white/30 transition-colors"
                  aria-label="Previous persona"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {/* Center: Persona Info */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activePersona?.id ?? activeIndex}
                    className="flex-1 flex flex-col items-center text-center gap-4 min-w-0"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.22, ease: "easeInOut" }}
                  >
                    <h3 className="text-3xl font-bold text-white drop-shadow-sm leading-tight">
                      {activePersona?.title ?? ""}
                    </h3>
                    <p className="text-sm text-white/80 leading-relaxed px-2">
                      {activePersona?.description ?? ""}
                    </p>

                    {/* Gender Toggle */}
                    <div className="flex items-center gap-1 bg-white/20 rounded-full p-1 mt-2">
                      <button
                        onClick={() => setPendingGender("male")}
                        className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                          pendingGender === "male"
                            ? "bg-white text-gray-800 shadow"
                            : "text-white/80"
                        }`}
                      >
                        {maleLabel}
                      </button>
                      <button
                        onClick={() => setPendingGender("female")}
                        className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                          pendingGender === "female"
                            ? "bg-white text-gray-800 shadow"
                            : "text-white/80"
                        }`}
                      >
                        {femaleLabel}
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Right Arrow */}
                <button
                  onClick={handleNext}
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white active:bg-white/30 transition-colors"
                  aria-label="Next persona"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              {/* Dot Indicators */}
              <div className="flex items-center justify-center gap-2 mt-6 mb-6">
                {options.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    className={`rounded-full transition-all duration-300 ${
                      i === activeIndex
                        ? "w-4 h-2 bg-white"
                        : "w-2 h-2 bg-white/40"
                    }`}
                    aria-label={`Go to persona ${i + 1}`}
                  />
                ))}
              </div>

              {/* Select Button */}
              <button
                onClick={handleSelect}
                className="w-full py-4 rounded-full font-semibold text-base text-gray-800 shadow-lg active:scale-95 transition-transform"
                style={{
                  background: "linear-gradient(135deg, #fde68a 0%, #f59e0b 100%)",
                }}
              >
                {selectLabel}
              </button>

              {/* Bottom safe area spacer */}
              <div style={{ height: "env(safe-area-inset-bottom, 0px)" }} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
